/**
 * Recover an Okta access token before tearing the session down.
 *
 * Why this exists (reported 2026-08-03 — clinicians dropped to the login page
 * mid-work with no countdown dialog):
 *
 *   - okta-auth-js removes an access token the moment it considers it expired,
 *     and `components/okta/config.ts` sets `expireEarlySeconds: 300` — so the
 *     token disappears FIVE MINUTES before its real `exp`, routinely while a
 *     renewal is still in flight.
 *   - `AutoRenewService.requiresLeadership()` returns true whenever
 *     `syncStorage` is on (the default), so renewal runs ONLY in the elected
 *     leader tab. A backgrounded leader has its timers throttled or frozen by
 *     the browser, so the renewal lands late — and the foreground tab the
 *     clinician is actually working in is the one that discovers "no token".
 *
 * Both are recoverable races. Treating them as session death produces exactly
 * the symptom reported: no dialog, no "Stay Logged In", mid-chart.
 *
 * The inactivity countdown in `components/okta/okta-token-renewal.component.tsx`
 * is unaffected — it remains the ONLY intended way a session ends on idle.
 */

let inFlight: Promise<string | null> | null = null

/**
 * Renew the access token, at most once concurrently. Resolves to the new access
 * token, or null when Okta has nothing left to renew into (dead refresh token,
 * revoked session, blocked renewal iframe).
 *
 * Deduplicated on purpose: a page fires many parallel requests and they all
 * discover the same missing token at the same moment. Okta throttles renew calls
 * and emits "Too many token renew requests" — a stampede would convert a
 * recoverable race into the hard logout this module exists to prevent.
 */
export const renewAccessTokenOnce = (oktaAuth: any): Promise<string | null> => {
  if (!oktaAuth?.tokenManager?.renew) return Promise.resolve(null)
  if (!inFlight) {
    inFlight = Promise.resolve()
      .then(() => oktaAuth.tokenManager.renew('accessToken'))
      .then((token: any) => token?.accessToken || oktaAuth.getAccessToken?.() || null)
      .catch(() => null)
      .finally(() => {
        inFlight = null
      })
  }
  return inFlight
}

const RETRY_FLAG = '__atmanAuthRetried'

/**
 * May this 401 be retried after a renewal?
 *
 * No for a request already replayed once — a second 401 means the credential is
 * genuinely rejected, not stale, and retrying again would loop.
 *
 * No for `/auth/token-verification` — a 401 there is Okta's own introspection
 * rejecting the access token, so there is nothing to renew into and a retry
 * would hammer the mint endpoint on a dead session.
 */
export const canRetryAfter401 = (config: any): boolean => {
  if (!config || config[RETRY_FLAG]) return false
  return !String(config.url || '').endsWith('/token-verification')
}

export const markRetried = (config: any): void => {
  if (config) config[RETRY_FLAG] = true
}

/** Test seam — the in-flight dedupe is module state and would leak across cases. */
export const __resetRenewalStateForTests = (): void => {
  inFlight = null
}
